/**
 * The anonymous visitor id shared by page-view analytics, heatmaps and A/B
 * testing, so all three agree on who a visitor is.
 *
 * KIT's own hosted sites get the `kit_vid` cookie from their middleware;
 * nothing sets it here, so it is minted client-side. It is a random UUID: no
 * name, no email, nothing that identifies a person.
 */

const VISITOR_COOKIE = 'kit_vid';
const ONE_YEAR = 60 * 60 * 24 * 365;

export function readCookie(name: string): string {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : '';
}

/** True only while the visitor's analytics consent state allows it (see src/lib/consent.ts). Unknown means no. */
function analyticsGranted(): boolean {
  try {
    return (window as unknown as { __kitConsent?: { analytics_storage?: string } }).__kitConsent?.analytics_storage === 'granted';
  } catch {
    return false;
  }
}

/**
 * The API wants a short alphanumeric/hyphen token, which a UUID already is.
 *
 * Returns '' (and sets no cookie) unless analytics is allowed for this visitor, so visitors who have not
 * consented where consent is required are neither identified nor counted by the site's own analytics.
 */
export function visitorId(): string {
  if (!analyticsGranted()) return '';
  const existing = readCookie(VISITOR_COOKIE);
  if (existing) return existing;

  const id =
    globalThis.crypto?.randomUUID?.() ??
    `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

  document.cookie = `${VISITOR_COOKIE}=${id}; Max-Age=${ONE_YEAR}; Path=/; SameSite=Lax`;
  return id;
}
